import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AlertCircle, Banknote, BadgeCheck, CheckCheck, CreditCard, Flame, Loader2, Pencil, Phone, Receipt, ShoppingBag, Star } from "lucide-react";
import { toast } from "sonner";
import { useShop } from "@/context/ShopContext";
import { AppButton, Card, EmptyState, ScreenHeader } from "@/components/AppUI";
import { StatusPill } from "@/pages/MyOrders";
import { SIZE_LABELS, euro, formatError, papi, saveOrderId, timeLabel } from "@/lib/pizzeria";

const STEPS = [
  { key: "nuovo", label: "Ricevuto", icon: Receipt },
  { key: "in_preparazione", label: "In forno", icon: Flame },
  { key: "pronto", label: "Pronto", icon: ShoppingBag },
  { key: "ritirato", label: "Ritirato", icon: CheckCheck },
];
const muted = { color: "var(--muted-c)" };

const Steps = ({ status }) => {
  const idx = STEPS.findIndex((s) => s.key === status);
  return (
    <div className="flex justify-between" data-testid="order-steps">
      {STEPS.map(({ key, label, icon: Icon }, i) => {
        const done = i <= idx;
        return (
          <div key={key} className="flex-1 flex flex-col items-center gap-1.5">
            <span className="h-10 w-10 rounded-full grid place-items-center" style={done ? { background: "var(--brand)", color: "var(--on-brand)" } : { background: "var(--surface-3)", color: "var(--muted-c)" }}><Icon className="h-[18px] w-[18px]" /></span>
            <span className="text-xs font-medium" style={done ? {} : muted}>{label}</span>
          </div>
        );
      })}
    </div>
  );
};

const Payment = ({ o, reload }) => {
  const [busy, setBusy] = useState("");
  const paid = o.payment_status === "paid";
  const payOnline = async () => {
    setBusy("online");
    try {
      const { data } = await papi.post(`/orders/${o.id}/checkout-session`, { origin: window.location.origin });
      window.location.href = data.url || data.checkout_url;
    } catch (e) {
      toast.error(formatError(e));
      setBusy("");
    }
  };
  const payPickup = async () => {
    setBusy("ritiro");
    try {
      await papi.post(`/orders/${o.id}/pay-at-pickup`);
      toast.success(`Ordine #${o.code} inviato: paghi al ritiro`);
      reload();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy("");
    }
  };
  const Icon = o.payment_method === "online" ? CreditCard : Banknote;
  const text = o.payment_status === "refunded" ? `Rimborsato ${euro(o.total)}: arriva sulla tua carta in pochi giorni`
    : paid ? `Pagato online ${euro(o.total)} ✓`
    : o.payment_method === "online" ? "Pagamento non completato"
    : `Pagherai ${euro(o.total)} al ritiro in pizzeria`;
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3 p-4 rounded-xl" style={{ background: "var(--surface-3)" }} data-testid="order-detail-payment"><Icon className="h-[22px] w-[22px]" style={{ color: "var(--brand)" }} /><span className="text-sm font-medium">{text}</span></div>
      {o.payment_method === "online" && !paid && o.payment_status !== "refunded" && o.status !== "annullato" && (
        <div className="space-y-2" data-testid="order-pending-payment">
          <AppButton icon={CreditCard} title="Paga ora online" loading={busy === "online"} disabled={!!busy} onClick={payOnline} testId="order-pay-online-button" className="w-full" />
          <AppButton variant="outline" icon={Banknote} title="Paga al ritiro" loading={busy === "ritiro"} disabled={!!busy} onClick={payPickup} testId="order-pay-pickup-button" className="w-full" />
        </div>
      )}
    </div>
  );
};

export default function OrderStatus() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { settings } = useShop();
  const [o, setO] = useState(null);
  const [error, setError] = useState(false);
  const confirmed = useRef(false);

  const load = useCallback(() => papi.get(`/orders/${id}`).then(({ data }) => { setO(data); saveOrderId(id); }).catch(() => setError(true)), [id]);

  useEffect(() => {
    const sid = params.get("session_id");
    if (sid && !confirmed.current) {
      confirmed.current = true;
      papi.post(`/orders/${id}/confirm-payment`, {}, { params: { session_id: sid } }).then(({ data }) => toast.success(`Ordine #${data?.code ?? data?.order?.code ?? ""} pagato online!`)).catch(() => {}).finally(load);
    } else {
      if (params.get("canceled") || params.get("cancelled") || params.get("payment") === "cancelled") {
        setTimeout(() => toast("Pagamento annullato: puoi riprovare o pagare al ritiro."), 300);
      }
      load();
    }
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [id, params, load]);

  const phone = settings?.store?.phone;
  return (
    <div className="flex-1" data-testid="order-status-page">
      <ScreenHeader title="Dettaglio ordine" fallback="/ordini" />
      {error ? <EmptyState icon={AlertCircle} title="Ordine non trovato" testId="order-not-found" /> : !o ? <Loader2 className="h-6 w-6 animate-spin mx-auto mt-12" style={{ color: "var(--brand)" }} /> : (
        <div className="p-4 pb-8 space-y-4">
          <div className="rounded-[20px] p-6 flex flex-col items-center gap-2" style={{ background: "var(--brand)", color: "var(--on-brand)" }}>
            <p className="text-xs uppercase tracking-widest opacity-70">Numero ordine</p>
            <p className="text-5xl font-medium" data-testid="order-detail-code">#{o.code}</p>
            <StatusPill status={o.status} testId="order-detail-status" />
            <p className="text-sm opacity-85" data-testid="order-pickup-time">Ritiro {timeLabel(o.pickup_time)}</p>
          </div>
          {o.status === "annullato" ? <p className="text-center text-sm font-medium" style={{ color: "var(--brand)" }}>Ordine annullato</p> : <Steps status={o.status} />}
          <Card className="px-4">
            {(o.items || []).map((x, i) => (
              <div key={i} className="flex justify-between gap-3 py-3" style={i ? { borderTop: "1px solid var(--border-c)" } : {}}>
                <div className="min-w-0"><p className="text-base font-medium">{x.quantity}× {x.name}{x.size ? `  ${SIZE_LABELS[x.size] || x.size}` : ""}</p>
                  {(x.ingredients || x.removed?.length > 0) && <p className="text-xs whitespace-pre-line" style={muted} data-testid={`order-item-builder-${i}`}>{x.ingredients || `SENZA: ${x.removed.join(", ")}`}</p>}
                </div>
                <p className="text-sm font-medium shrink-0">{euro((x.unit_price || 0) * x.quantity)}</p>
              </div>
            ))}
            {o.loyalty_reward && <div className="flex justify-between py-3 text-sm" style={{ borderTop: "1px solid var(--border-c)", color: "var(--success)" }}><span>Premio fedeltà (pizza in omaggio)</span><span>-{euro(o.loyalty_discount ?? o.discount)}</span></div>}
            {(o.promos || []).map((pr, i) => <div key={i} className="flex justify-between py-3 text-sm" style={{ borderTop: "1px solid var(--border-c)", color: "var(--success)" }} data-testid={`order-promo-${i}`}><span>{pr.name || "Sconto"}{pr.percent ? ` -${pr.percent}%` : ""}</span><span>{pr.amount != null || pr.discount != null ? `-${euro(pr.amount ?? pr.discount)}` : ""}</span></div>)}
            {!o.loyalty_reward && !o.promos?.length && o.discount > 0 && <div className="flex justify-between py-3 text-sm" style={{ borderTop: "1px solid var(--border-c)", color: "var(--success)" }}><span>Sconto</span><span>-{euro(o.discount)}</span></div>}
            <div className="flex justify-between items-center py-3" style={{ borderTop: "1px solid var(--border-c)" }}><span>Totale</span><span className="text-xl font-medium" data-testid="order-total">{euro(o.total)}</span></div>
          </Card>
          {o.notes && <Card className="p-4"><p className="text-sm font-medium">Note</p><p className="text-sm mt-1" style={muted}>{o.notes}</p></Card>}
          <Payment o={o} reload={load} />
          {o.change_request && (
            <div className="p-4 rounded-xl text-sm space-y-1" style={{ background: "var(--brand-3)", color: "var(--on-brand-3)" }} data-testid="order-change-status">
              <p className="font-medium">{{ pending: "Modifica richiesta: in attesa della pizzeria", accepted: "Modifica accettata", approved: "Modifica accettata", rejected: "Modifica rifiutata" }[o.change_request.status] || "Modifica richiesta"}</p>
              {o.change_request.reply && <p>{o.change_request.reply}</p>}
            </div>
          )}
          {["nuovo", "in_preparazione"].includes(o.status) && o.change_request?.status !== "pending" && <AppButton variant="outline" icon={Pencil} title="Richiedi una modifica" onClick={() => nav(`/order/${o.id}/modifica`)} testId="order-change-button" className="w-full" />}
          {["nuovo", "in_preparazione"].includes(o.status) && (
            <div className="flex items-center gap-3 p-4 rounded-xl" style={{ background: "var(--surface-3)" }} data-testid="order-cancel-info">
              <span className="flex-1 text-sm">Vuoi annullare l'ordine? Chiama la pizzeria.</span>
              {phone && <a href={`tel:${phone}`}><AppButton small variant="dark" icon={Phone} title={`Chiama ${phone}`} testId="order-call-button" /></a>}
            </div>
          )}
          {o.status === "ritirato" && !o.has_feedback && <AppButton icon={Star} title="Lascia una recensione" onClick={() => nav(`/feedback/${o.id}`)} testId="order-detail-feedback-button" className="w-full" />}
          {o.status === "pronto" && <p className="flex items-center justify-center gap-2 text-sm font-medium" style={{ color: "var(--success)" }}><BadgeCheck className="h-5 w-5" />La tua pizza è pronta: passa a ritirarla!</p>}
        </div>
      )}
    </div>
  );
}
